import {
  Button,
  DatePicker,
  IconCheckmarkCircle,
  Modal,
  ProgressIndicator,
  RadioButton,
  RadioButtons,
  Select,
  SelectOption,
  TextArea,
  TextBox,
} from "@veneer/core";
import moment from "moment";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState } from "../../app/store";
import appointmentService from "../../services/agendaApi/appointment.service";
import supportService from "../../services/supportApi/support.service";
import { decryptText } from "../../utils/encryptionUtils";
import "../../assets/css/views/support/SupportTicketForm.css";

interface ISupportIssue {
  id: number;
  label: string;
  requiresFreeText: boolean;
  key: string;
}

interface ISupportOption {
  id: number;
  label: string;
  key: string;
  issues: ISupportIssue[];
}

interface IFieldDto {
  fieldID: string | number;
  fieldValue: string;
}

interface SupportTicketFormProps {
  onBack: () => void;
}

const SupportTicketForm = ({ onBack }: SupportTicketFormProps) => {
  const [t, i18n] = useTranslation("global");
  const userState: any = useSelector((state: RootState) => state.user);

  const [supportOptions, setSupportOptions] = useState<ISupportOption[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [optionsError, setOptionsError] = useState(false);

  const [activity, setActivity] = useState<ISupportOption | null>(null);
  const [activityValue, setActivityValue] = useState<any[]>([]);
  const [issue, setIssue] = useState<ISupportIssue | null>(null);
  const [issueValue, setIssueValue] = useState<any[]>([]);
  const [freeText, setFreeText] = useState("");
  const [description, setDescription] = useState("");
  const [email, setEmail] = useState("");
  const [platform, setPlatform] = useState("WEB");
  const [incidentDate, setIncidentDate] = useState<moment.Moment | null>(null);
  const [selectedPos, setSelectedPos] = useState<IFieldDto | null>(null);

  const [showPosModal, setShowPosModal] = useState(false);
  const [subretailList, setSubretailList] = useState<SelectOption[]>([]);
  const [posList, setPosList] = useState<SelectOption[]>([]);
  const [posSubretailValue, setPosSubretailValue] = useState<any[]>([]);
  const [posValue, setPosValue] = useState<any[]>([]);
  const [posLoading, setPosLoading] = useState(false);
  const [posRawList, setPosRawList] = useState<IFieldDto[]>([]);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [ticketNumber, setTicketNumber] = useState<string | null>(null);

  useEffect(() => {
    const loadOptions = async () => {
      setOptionsLoading(true);
      setOptionsError(false);
      try {
        const response = await supportService.getSupportOptions(Number(t("header.idLanguage")), i18n.language);
        if (Array.isArray(response?.support)) setSupportOptions(response.support);
      } catch (error) {
        console.error("Error loading support options:", error);
        setOptionsError(true);
      } finally {
        setOptionsLoading(false);
      }
    };
    loadOptions();
  }, [i18n.language]);

  const openPosModal = async () => {
    setShowPosModal(true);
    if (subretailList.length === 0) {
      setPosLoading(true);
      try {
        const r = await appointmentService.getSubretailList();
        setSubretailList(
          (r || [])
            .filter((item: IFieldDto) => item.fieldID !== 0)
            .map((item: IFieldDto) => ({ value: item.fieldID, label: item.fieldValue }))
        );
      } catch (error) {
        // keep list empty; user can retry by reopening
        console.error("Error loading subretail list:", error);
      } finally {
        setPosLoading(false);
      }
    }
  };

  const handleSubretailChange = async (e: any) => {
    setPosSubretailValue([e.value]);
    setPosValue([]);
    setPosList([]);
    setSelectedPos(null);
    setPosLoading(true);
    try {
      const r: IFieldDto[] = await appointmentService.getPosListBySubretailList(String(e.value));
      const raw = r || [];
      setPosRawList(raw);
      setPosList(
        raw.filter((i) => i.fieldID).map((i) => ({ value: i.fieldID, label: i.fieldValue }))
      );
    } catch (error) {
      console.error("Error loading POS list:", error);
      setPosList([]);
    } finally {
      setPosLoading(false);
    }
  };

  const handlePosSelect = (e: any) => {
    setPosValue([e.value]);
    const raw = posRawList.find((i) => String(i.fieldID) === String(e.value));
    if (raw) setSelectedPos(raw);
  };

  const confirmPosSelection = () => {
    setShowPosModal(false);
    setPosSubretailValue([]);
    setPosValue([]);
    setPosList([]);
    setErrors((prev) => ({ ...prev, pos: "" }));
  };

  const closePosModal = () => {
    setShowPosModal(false);
    setPosSubretailValue([]);
    setPosValue([]);
    setPosList([]);
  };

  const handleActivityChange = (e: any) => {
    setActivity(supportOptions.find((o) => o.id === e.value) || null);
    setActivityValue([e.value]);
    setIssue(null);
    setIssueValue([]);
    setFreeText("");
    setErrors((prev) => ({ ...prev, activity: "", issue: "", freeText: "" }));
  };

  const handleIssueChange = (e: any) => {
    setIssue(activity?.issues.find((i) => i.id === e.value) || null);
    setIssueValue([e.value]);
    setFreeText("");
    setErrors((prev) => ({ ...prev, issue: "", freeText: "" }));
  };

  const emailRegex = /^[^\s@]+@[^\s@.]+(?:\.[^\s@.]+)+$/;

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    const fieldRequiredMsg = t("support.fieldRequired");
    if (!activity) e.activity = fieldRequiredMsg;
    if (!issue) e.issue = fieldRequiredMsg;
    if (issue?.requiresFreeText && freeText.trim().length < 3)
      e.freeText = t("support.freeTextMin");
    if (!email || !emailRegex.test(email)) e.email = t("support.emailInvalid");
    if (!incidentDate) e.incidentDate = fieldRequiredMsg;
    if (description.trim().length < 15) e.description = t("support.descriptionMin");
    else if (description.trim().length > 800) e.description = t("support.descriptionMax");
    if (!selectedPos) e.pos = fieldRequiredMsg;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const buildShortDescription = (activity: ISupportOption, issue: ISupportIssue, freeText: string): string => {
    const isOtherOpen =
      activity.key === "ACT.OTRO" &&
      issue.key === "ISS.OTHER.OPEN" &&
      issue.requiresFreeText;
    let short =
      isOtherOpen && freeText
        ? `User reported an additional issue: "${freeText}".`
        : `User reported an issue under "${activity.label}" regarding "${issue.label}". Please review and provide assistance.`;
    if (short.length > 200) short = short.substring(0, 199) + "…";
    return short;
  };

  const buildDescription = (selectedPos: IFieldDto): string =>
    `User email: ${email}\nPlatform: ${platform}\nIncident date: ${moment(incidentDate).format("YYYY-MM-DD")}\nPOS Code: ${selectedPos.fieldID}\nPOS Name: ${selectedPos.fieldValue}\n\nUser description:\n${description}`;

  const handleSubmit = async () => {
    if (!validate()) return;
    if (!activity || !issue || !selectedPos) return;
    setSubmitting(true);
    setErrors((prev) => ({ ...prev, submit: "" }));
    try {
      const userName =
        decryptText(userState.user.name) + " " + decryptText(userState.user.lastName);
      const result = await supportService.sendSupport({
        shortDescription: buildShortDescription(activity, issue, freeText),
        description: buildDescription(selectedPos),
        userName,
      });
      setTicketNumber(typeof result === "string" ? result : String(result));
    } catch (error) {
      console.error("Error submitting support ticket:", error);
      setErrors((prev) => ({ ...prev, submit: t("support.submitError") }));
    } finally {
      setSubmitting(false);
    }
  };

  if (ticketNumber) {
    return (
      <div className="support-ticket-success">
        <IconCheckmarkCircle size={64} color="colorLightGreen6" filled />
        <h3>{t("support.successTitle")}</h3>
        <p>
          {t("support.successMessage")} <strong>{ticketNumber}</strong>
        </p>
        <Button onClick={onBack}>{t("support.back")}</Button>
      </div>
    );
  }

  if (optionsLoading) {
    return (
      <div className="support-ticket-loading">
        <ProgressIndicator appearance="circular" behavior="indeterminate" />
      </div>
    );
  }

  if (optionsError) {
    return (
      <div className="support-ticket-error-state">
        <p>{t("support.optionsLoadError")}</p>
        <Button appearance="secondary" onClick={onBack}>
          {t("support.back")}
        </Button>
      </div>
    );
  }

  const activityOptions: SelectOption[] = supportOptions.map((o) => ({
    value: o.id,
    label: o.label,
  }));
  const issueOptions: SelectOption[] =
    activity?.issues.map((i) => ({ value: i.id, label: i.label })) || [];

  const isFormValid =
    !!activity &&
    !!issue &&
    (!issue.requiresFreeText || freeText.trim().length >= 3) &&
    emailRegex.test(email) &&
    !!incidentDate &&
    description.trim().length >= 15 &&
    description.trim().length <= 800 &&
    !!selectedPos &&
    !Object.entries(errors).some(([k, v]) => k !== "submit" && !!v);

  return (
    <>
      <div className="support-ticket-body">
        <p className="support-ticket-section-title">{t("support.sectionIssue")}</p>

            <div className="support-ticket-field">
              <Select
                id="support-activity"
                label={t("support.activity")}
                separateLabel
                placeholder={t("support.selectActivity")}
                options={activityOptions}
                value={activityValue}
                placement="bottom"
                onChange={handleActivityChange}
                onClear={() => {
                  setActivity(null);
                  setActivityValue([]);
                  setIssue(null);
                  setIssueValue([]);
                }}
                disabled={submitting}
                required
                error={!!errors.activity}
              />
              {errors.activity && <p className="support-field-error">{errors.activity}</p>}
            </div>

            {activity && (
              <div className="support-ticket-field">
                <Select
                  id="support-issue"
                  label={t("support.issue")}
                  separateLabel
                  placeholder={t("support.selectIssue")}
                  options={issueOptions}
                  value={issueValue}
                  placement="bottom"
                  onChange={handleIssueChange}
                  onClear={() => {
                    setIssue(null);
                    setIssueValue([]);
                    setFreeText("");
                  }}
                  disabled={submitting}
                  required
                  error={!!errors.issue}
                />
                {errors.issue && <p className="support-field-error">{errors.issue}</p>}
              </div>
            )}

            {issue?.requiresFreeText && (
              <div className="support-ticket-field">
                <TextBox
                  id="support-freetext"
                  label={t("support.freeText")}
                  placeholder={t("support.freeText")}
                  value={freeText}
                  onChange={(v: string) => {
                    setFreeText(v);
                    if (errors.freeText && v.trim().length >= 3) setErrors((p) => ({ ...p, freeText: "" }));
                  }}
                  disabled={submitting}
                  required
                  error={!!errors.freeText}
                />
                {errors.freeText && <p className="support-field-error">{errors.freeText}</p>}
              </div>
            )}

            <div className="support-ticket-field">
              <button
                type="button"
                className="support-pos-selector"
                disabled={submitting}
                onClick={openPosModal}
              >
                <TextBox
                  id="support-pos-display"
                  label={t("support.pos")}
                  separateLabel
                  placeholder={t("support.selectPos")}
                  value={
                    selectedPos
                      ? `${selectedPos.fieldID} - ${selectedPos.fieldValue}`
                      : ""
                  }
                  readOnly
                  required
                  error={!!errors.pos}
                />
              </button>
              <p className="support-field-helper">{t("support.posHelper")}</p>
              {errors.pos && <p className="support-field-error">{errors.pos}</p>}
            </div>

            <p className="support-ticket-section-title">{t("support.sectionContact")}</p>

            <div className="support-ticket-field">
              <TextBox
                id="support-email"
                label={t("support.emailLabel")}
                separateLabel
                placeholder={t("support.emailPlaceholder")}
                value={email}
                onChange={(v: string) => {
                  setEmail(v);
                  if (errors.email && emailRegex.test(v)) setErrors((p) => ({ ...p, email: "" }));
                }}
                onBlur={() => {
                  if (email && !emailRegex.test(email)) setErrors((p) => ({ ...p, email: t("support.emailInvalid") }));
                }}
                disabled={submitting}
                required
                error={!!errors.email}
              />
              {errors.email && <p className="support-field-error">{errors.email}</p>}
            </div>

            <div className="support-ticket-row">
              <div className="support-ticket-field">
                <p className="support-ticket-label">{t("support.platform")} *</p>
                <RadioButtons
                  name="support-platform"
                  value={platform}
                  onChange={(_e: any, v: string) => setPlatform(v)}
                >
                  <RadioButton label="WEB" value="WEB" />
                  <RadioButton label="MOBILE" value="MOBILE" />
                </RadioButtons>
                <p className="support-field-helper">{t("support.platformHelper")}</p>
              </div>

              <div className="support-ticket-field">
                <DatePicker
                  className="support-incident-date"
                  label={t("support.incidentDate")}
                  separateLabel
                  calendarOrientation={["right", "down"]}
                  placeholder={t("support.incidentDatePlaceholder")}
                  value={incidentDate ?? undefined}
                  onChange={(d: any) => {
                    setIncidentDate(d);
                    setErrors((p) => ({ ...p, incidentDate: "" }));
                  }}
                  isOutsideRange={(d: any) => d.isAfter(moment(), "day")}
                  disabled={submitting}
                  required
                  error={!!errors.incidentDate}
                />
                <p className="support-field-helper">{t("support.incidentDateHelper")}</p>
                {errors.incidentDate && <p className="support-field-error">{errors.incidentDate}</p>}
              </div>
            </div>

            <p className="support-ticket-section-title">{t("support.sectionIncident")}</p>

            <div className="support-ticket-field">
              <TextArea
                id="support-description"
                label={`${t("support.descriptionLabel")} (${description.length}/800)`}
                separateLabel
                placeholder={t("support.descriptionPlaceholder")}
                value={description}
                onChange={(v: string) => {
                  setDescription(v);
                  if (errors.description) {
                    const len = v.trim().length;
                    if (len >= 15 && len <= 800) setErrors((p) => ({ ...p, description: "" }));
                  }
                }}
                onBlur={() => {
                  const len = description.trim().length;
                  if (len > 0 && len < 15) setErrors((p) => ({ ...p, description: t("support.descriptionMin") }));
                  else if (len > 800) setErrors((p) => ({ ...p, description: t("support.descriptionMax") }));
                }}
                disabled={submitting}
                required
                error={!!errors.description}
              />
              <p className="support-field-helper">{t("support.descriptionTip")}</p>
              {errors.description && <p className="support-field-error">{errors.description}</p>}
            </div>

        {errors.submit && (
          <p className="support-ticket-submit-error">{errors.submit}</p>
        )}

        <div className="support-ticket-actions">
          <Button onClick={handleSubmit} disabled={submitting || !isFormValid}>
            {submitting ? (
              <ProgressIndicator appearance="circular" behavior="indeterminate" />
            ) : (
              t("support.sendIssueButton")
            )}
          </Button>
        </div>
      </div>

      <Modal
        align="start"
        id="support-pos-modal"
        closeButton
        show={showPosModal}
        onClose={closePosModal}
        title={t("support.selectPos")}
        footer={
          <div style={{ textAlign: "center" }}>
            <Button
              disabled={!selectedPos || posValue.length === 0}
              onClick={confirmPosSelection}
            >
              {t("support.confirmPos")}
            </Button>
          </div>
        }
      >
        {posLoading ? (
          <ProgressIndicator appearance="circular" behavior="indeterminate" />
        ) : (
          <>
            <Select
              id="support-subretail"
              label={t("support.subretail")}
              placeholder={t("support.selectSubretail")}
              options={subretailList}
              value={posSubretailValue}
              placement="bottom"
              onChange={handleSubretailChange}
              onClear={() => {
                setPosSubretailValue([]);
                setPosList([]);
                setPosValue([]);
                setSelectedPos(null);
              }}
            />
            {posList.length > 0 && (
              <>
                <br />
                <Select
                  id="support-pos-select"
                  label={t("support.pos")}
                  placeholder={t("support.selectPos")}
                  options={posList}
                  value={posValue}
                  placement="bottom"
                  onChange={handlePosSelect}
                  onClear={() => {
                    setPosValue([]);
                    setSelectedPos(null);
                  }}
                />
              </>
            )}
          </>
        )}
      </Modal>
    </>
  );
};

export default SupportTicketForm;
